import { getSupplier } from './api/suppliers';
import { getRetailer, getRetailerSuppliers } from './api/retailers';
import { fetchWithRetry } from '../../utils/api';
import history from '../../utils/history';
import { setRole, setRolesUpdateRole, setRolesRetailerSuppliers } from './roles';
import { SUPPLIER, RETAILER } from '../../constants/roles';

import {
  CHECK_PERMISSIONS_LOADING,
  CHECK_PERMISSIONS_RETAILER,
  SET_PERMISSION_STATUS,
} from '../constants/checkPermissionsTypes';
import { PERMISSION_STATUS } from '../../constants/permissions';

export const setCheckPermissionsDenied = { type: SET_PERMISSION_STATUS, payload: { status: PERMISSION_STATUS.DENIED } };
export const setCheckPermissionsServerError = {
  type: SET_PERMISSION_STATUS,
  payload: { status: PERMISSION_STATUS.SERVER_ERROR },
};
export const setCheckPermissionsLoading = (value) => ({ type: CHECK_PERMISSIONS_LOADING, payload: value });
export const setRetailerPermissions = (permissions) => ({ type: CHECK_PERMISSIONS_RETAILER, payload: permissions });

const fetchSupplier = async (roleId, dispatch) => {
  const supplierResponse = await getSupplier(roleId);
  return supplierResponse.data;
};

const fetchRetailer = async (roleId, dispatch) => {
  const retailerResponse = await getRetailer(roleId);
  return retailerResponse.data;
};

const setRetailerSuppliers = async (retailerId, dispatch) => {
  const response = await getRetailerSuppliers(retailerId);
  if (!response) {
    return [];
  }
  const retailerSuppliers = response.data;
  dispatch(setRolesRetailerSuppliers(retailerSuppliers));
  return retailerSuppliers;
};

export const checkPermissions = (roleType, roleId, checkAdmin, reportToMixpanel) => async (dispatch, getState) => {
  const { role } = getState();
  const { user } = role;

  if (!user) {
    dispatch(setCheckPermissionsDenied);
    reportToMixpanel({ href: window.location.href, role, user, roleType, checkAdmin, ifLocation: '!user' });
    return;
  }

  const { suppliers, retailers, is_super_admin } = user;

  dispatch(setCheckPermissionsLoading(true));

  if (roleType === SUPPLIER) {
    let supplier = role.supplier;

    // check if no supplier or different supplier
    if (!supplier || supplier.id !== roleId) {
      supplier = await fetchWithRetry(
        () => fetchSupplier(roleId, dispatch),
        PERMISSION_STATUS.SERVER_ERROR,
        2000,
        3,
        reportToMixpanel,
      );
    }

    if (supplier === PERMISSION_STATUS.SERVER_ERROR) {
      dispatch(setCheckPermissionsServerError);
      reportToMixpanel({
        href: window.location.href,
        role,
        user,
        roleType,
        checkAdmin,
        supplier,
        ifLocation: 'supplier === PERMISSION_STATUS.SERVER_ERROR',
      });
      return;
    }

    if (!supplier) {
      dispatch(setCheckPermissionsDenied);
      reportToMixpanel({
        href: window.location.href,
        role,
        user,
        roleType,
        checkAdmin,
        supplier,
        ifLocation: '!supplier',
      });
      return;
    }

    if (!is_super_admin) {
      const supplierPermission = suppliers.find(({ id }) => id === roleId);
      if (!supplierPermission) {
        dispatch(setCheckPermissionsDenied);
        reportToMixpanel({
          href: window.location.href,
          role,
          user,
          roleType,
          checkAdmin,
          ifLocation: 'is_super_admin.!supplierPermission',
        });
        return;
      }

      if (checkAdmin && !supplierPermission.is_admin) {
        history.push(`/suppliers/${supplier.id}/retailers`);
        return;
      }
    }

    dispatch(setRolesUpdateRole(roleId, SUPPLIER));
    dispatch(setRole(SUPPLIER, supplier));
    setTimeout(() => dispatch(setCheckPermissionsLoading(false)));
  } else if (roleType === RETAILER) {
    let retailer = role.retailer;

    // check if no retailer or different retailer
    if (!retailer || retailer.id !== roleId) {
      retailer = await fetchWithRetry(
        () => fetchRetailer(roleId, dispatch),
        PERMISSION_STATUS.SERVER_ERROR,
        2000,
        3,
        reportToMixpanel,
      );
    }

    if (retailer === PERMISSION_STATUS.SERVER_ERROR) {
      dispatch(setCheckPermissionsServerError);
      reportToMixpanel({
        href: window.location.href,
        role,
        user,
        roleType,
        checkAdmin,
        retailer,
        ifLocation: 'retailer === PERMISSION_STATUS.SERVER_ERROR',
      });
      return;
    }

    if (!retailer) {
      dispatch(setCheckPermissionsDenied);
      reportToMixpanel({
        href: window.location.href,
        role,
        user,
        roleType,
        checkAdmin,
        retailer,
        ifLocation: '!retailer',
      });
      return;
    }

    if (!is_super_admin) {
      const hasRetailerPermission = await checkRetailerPermissions(retailer.id, dispatch, getState);
      if (!hasRetailerPermission) {
        dispatch(setCheckPermissionsDenied);
        return;
      }

      const retailerPermission = retailers.find(({ id }) => id === roleId);
      if (!retailerPermission || (checkAdmin && retailerPermission.administration_permission === 'none')) {
        dispatch(setCheckPermissionsDenied);
        return;
      }
    } else {
      await setRetailerSuppliers(roleId, dispatch);
      dispatch(
        setRetailerPermissions({
          dropship: true,
          replenish: true,
          administration: true,
        }),
      );
    }

    dispatch(setRole(RETAILER, retailer));
    dispatch(setRolesUpdateRole(roleId, RETAILER));
    setTimeout(() => dispatch(setCheckPermissionsLoading(false)));
  }
};

const checkRetailerPermissions = async (retailerId, dispatch, getState) => {
  const {
    location: { pathname },
  } = history;
  let { retailerSuppliers, user } = getState().role;

  const retailerPermissions = user.retailers.find(({ id }) => id === retailerId);

  if (!retailerSuppliers || !retailerSuppliers.length) {
    retailerSuppliers = await setRetailerSuppliers(retailerId, dispatch);
  }

  const approved_for_replenishment = retailerSuppliers.some((item) => item.approved_for_replenishment);
  const approved_for_ecommerce = retailerSuppliers.some((item) => item.approved_for_ecommerce);

  const hasDropshipPermissions = retailerPermissions.ecommerce_permission !== 'none' && approved_for_ecommerce;
  const hasReplenishPermissions = retailerPermissions.replenish_permission !== 'none' && approved_for_replenishment;
  const hasAdminPermissions = retailerPermissions.administration_permission !== 'none';

  dispatch(
    setRetailerPermissions({
      dropship: hasDropshipPermissions,
      replenish: hasReplenishPermissions,
      administration: hasAdminPermissions,
    }),
  );

  // check if retailer has no suppliers and redirect to /admin if not already there
  if (!retailerSuppliers.length) {
    if (!hasAdminPermissions && !pathname.includes('/nosuppliers')) {
      history.push(`/retailers/${retailerId}/nosuppliers`);
    } else if (hasAdminPermissions && !pathname.includes('/admin')) {
      history.push(`/retailers/${retailerId}/admin/brands`);
    } else {
      return true;
    }

    return false;
  }

  if (!hasReplenishPermissions && !hasDropshipPermissions && !pathname.includes('/admin')) {
    if (hasAdminPermissions) {
      history.push(`/retailers/${retailerId}/admin`);
    } else if (!pathname.includes('/nosuppliers')) {
      history.push(`/retailers/${retailerId}/nosuppliers`);
    } else {
      return true;
    }
  } else if (pathname.includes('/dropship') && !hasDropshipPermissions) {
    history.push(`/retailers/${retailerId}/reorder`);
  } else if (pathname.includes('/reorder') && !hasReplenishPermissions) {
    history.push(`/retailers/${retailerId}/dropship`);
  } else {
    return true;
  }

  dispatch(setCheckPermissionsDenied);
};

export const checkLoggedIn = () => async (dispatch, getState) => {
  const { user } = getState().role;

  if (!user) {
    dispatch(setCheckPermissionsDenied);
    return false;
  }

  dispatch(setCheckPermissionsLoading(false));
  return true;
};
